// =============================================================================
// useFeatures — read this rooftop's Battery Care feature flags.
// -----------------------------------------------------------------------------
// Group admins flip toggles in portal.sevric.com/store-settings under
// `apps.battery.features`. This hook subscribes to the active store doc and
// returns a merged-with-defaults feature map.
//
//   const features = useFeatures();
//   if (features.walk) <NavLink to="/walk" />
//
//   <RequireFeature name="walk">
//     <Walk />
//   </RequireFeature>
//
// First-time hydration (before snapshot lands) returns the defaults — every
// feature ON — so we never flash a hidden nav while waiting for Firestore.
// Disabled features hide nav links and redirect direct URL access to /today.
// =============================================================================

import { createElement, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase.js";
import { useActiveStore } from "./activeStore.js";

export const BATTERY_FEATURE_DEFAULTS = {
  // Pages
  today: true,
  walk: true,
  inventory: true,
  manager: true,
  addUnit: true,
  importDar: true,
  tvMode: true,
  report: true,
  chat: true,
  // Sub-features
  find: true,
  weeklyReport: true,
  aiInsights: true,
  bulkCheckIn: true,
  stickyNotes: true,
  voltage: true,
};

export function useFeatures() {
  const storeId = useActiveStore();
  const [features, setFeatures] = useState(BATTERY_FEATURE_DEFAULTS);

  useEffect(() => {
    if (!storeId) return undefined;
    const unsub = onSnapshot(
      doc(db, "stores", storeId),
      (snap) => {
        const f = snap.data()?.apps?.battery?.features || {};
        setFeatures({ ...BATTERY_FEATURE_DEFAULTS, ...f });
      },
      (err) => {
        // Don't blow up the app on a permissions/quota error — fall back to
        // defaults so users keep working until the read recovers.
        // eslint-disable-next-line no-console
        console.warn("[useFeatures] store read failed:", err?.message);
      }
    );
    return unsub;
  }, [storeId]);

  return features;
}

// useFeature(name) — convenience for single-flag checks. Treats "unknown
// flag" as enabled so a missing key in the defaults map doesn't accidentally
// hide a page.
export function useFeature(name) {
  const features = useFeatures();
  if (!(name in features)) return true;
  return features[name] !== false;
}

// Route gate. Disabled features bounce back to /today (the safe default page).
// Implemented with createElement so this file can stay .js (Vite + esbuild
// only treat .jsx as JSX by default in this project).
export function RequireFeature({ name, children, fallback = "/today" }) {
  const enabled = useFeature(name);
  if (!enabled) return createElement(Navigate, { to: fallback, replace: true });
  return children;
}
